/**
 * Row Grouping Plugin (Class-based)
 *
 * Enables hierarchical row grouping with expand/collapse and aggregations.
 */

import { GridClasses } from '../../core/constants';
import { aggregatorRegistry } from '../../core/internal/aggregators';
import { announce, getA11yMessage } from '../../core/internal/aria';
import { setRowLoadingState } from '../../core/internal/loading';
import {
  BaseGridPlugin,
  CellClickEvent,
  HeaderClickEvent,
  type GridElement,
  type PluginManifest,
  type PluginQuery,
} from '../../core/plugin/base-plugin';
import { isExpanderColumn } from '../../core/plugin/expander-column';
import type { RowElementInternal } from '../../core/types';
import type {
  DataSourceChildrenDetail,
  DataSourceDataDetail,
  FetchChildrenQuery,
  ViewportMappingQuery,
  ViewportMappingResponse,
} from '../server-side/datasource-types';
import {
  buildGroupedRowModel,
  buildPreDefinedGroupModel,
  collapseAllGroups,
  expandAllGroups,
  getGroupKeys,
  getGroupPath,
  getGroupRowCount,
  resolveDefaultExpanded,
  resolveGroupFields,
  toggleGroupExpansion,
} from './grouping-rows';
import styles from './grouping-rows.css?inline';
import type {
  DefaultExpandedValue,
  ExpandCollapseAnimation,
  GroupCollapseDetail,
  GroupDefinition,
  GroupExpandDetail,
  GroupingRowsConfig,
  GroupRowModelItem,
  GroupToggleDetail,
  RenderRow,
} from './types';

/**
 * Group state information returned by getGroupState()
 * @since 0.1.1
 */
export interface GroupState {
  /** Whether grouping is currently active */
  isActive: boolean;
  /** Number of expanded groups */
  expandedCount: number;
  /** Total number of groups */
  totalGroups: number;
  /** Array of expanded group keys */
  expandedKeys: string[];
}

/**
 * Row Grouping Plugin for tbw-grid
 *
 * Organizes rows into collapsible hierarchical groups. Perfect for organizing data
 * by category, department, status, or any other dimension—or even multiple dimensions
 * for nested grouping. Includes aggregation support for summarizing group data.
 *
 * ## Installation
 *
 * ```ts
 * import { GroupingRowsPlugin } from '@toolbox-web/grid/plugins/grouping-rows';
 * ```
 *
 * ## CSS Custom Properties
 *
 * | Property | Default | Description |
 * |----------|---------|-------------|
 * | `--tbw-group-indent-width` | `1.25em` | Indentation per group level |
 * | `--tbw-grouping-rows-bg` | `var(--tbw-color-panel-bg)` | Group row background |
 * | `--tbw-grouping-rows-count-color` | `var(--tbw-color-fg-muted)` | Count badge color |
 * | `--tbw-animation-duration` | `200ms` | Expand/collapse animation |
 *
 * @example Single-Level Grouping by Department
 * ```ts
 * import { queryGrid } from '@toolbox-web/grid';
 * import { GroupingRowsPlugin } from '@toolbox-web/grid/plugins/grouping-rows';
 *
 * const grid = queryGrid('tbw-grid');
 * grid.gridConfig = {
 *   columns: [
 *     { field: 'name', header: 'Employee' },
 *     { field: 'department', header: 'Department' },
 *     { field: 'salary', header: 'Salary', type: 'currency' },
 *   ],
 *   plugins: [
 *     new GroupingRowsPlugin({
 *       groupOn: (row) => [row.department],
 *       showRowCount: true,
 *       defaultExpanded: false,
 *     }),
 *   ],
 * };
 * ```
 *
 * @example Multi-Level Grouping
 * ```ts
 * new GroupingRowsPlugin({
 *   groupOn: (row) => [row.region, row.department, row.team],
 *   indentWidth: 24,
 *   animation: 'slide',
 * })
 * ```
 *
 * @see {@link GroupingRowsConfig} for all configuration options
 * @see {@link GroupState} for the group state structure
 *
 * @internal Extends BaseGridPlugin
 * @since 0.1.1
 */
export class GroupingRowsPlugin extends BaseGridPlugin<GroupingRowsConfig> {
  /**
   * Plugin manifest - declares configuration validation rules and events.
   * @internal
   */
  static override readonly manifest: PluginManifest<GroupingRowsConfig> = {
    modifiesRowStructure: true,
    hookPriority: {
      processRows: 10, // Run after ServerSide (-10) so we receive managedNodes[]
      // Run before MultiSort so we can intercept header clicks on grouped columns
      onHeaderClick: -1,
    },
    incompatibleWith: [
      {
        name: 'tree',
        reason:
          'Both plugins transform the entire row model. TreePlugin flattens nested hierarchies while ' +
          'GroupingRowsPlugin groups flat rows with synthetic headers. Use one approach per grid.',
      },
      {
        name: 'pivot',
        reason:
          'PivotPlugin creates its own aggregated row and column structure. ' +
          'Row grouping cannot be applied on top of pivot-generated rows.',
      },
    ],
    events: [
      {
        type: 'group-toggle',
        description: 'Emitted when groups are expanded/collapsed. Broadcast to both DOM consumers and plugin bus.',
      },
      {
        type: 'group-expand',
        description: 'Emitted when a pre-defined group is expanded.',
      },
      {
        type: 'group-collapse',
        description: 'Emitted when a pre-defined group is collapsed.',
      },
    ],
    queries: [
      {
        type: 'canMoveRow',
        description: 'Returns false for group header rows (cannot be reordered)',
      },
      {
        type: 'grouping:get-grouped-fields',
        description: 'Returns the column field names that match group depth levels (string[])',
      },
      {
        type: 'datasource:viewport-mapping',
        description: 'Translates flat viewport row indices to top-level group indices for ServerSide pagination.',
      },
    ],
    configRules: [
      {
        id: 'groupingRows/accordion-defaultExpanded',
        severity: 'warn',
        message:
          `"accordion: true" and "defaultExpanded" (non-false) are used together.\n` +
          `  → In accordion mode, only one group can be open at a time.\n` +
          `  → Using defaultExpanded with multiple groups will collapse to one on first toggle.\n` +
          `  → Consider using "defaultExpanded: false" or a single group key/index with accordion mode.`,
        check: (config) =>
          config.accordion === true &&
          config.defaultExpanded !== false &&
          config.defaultExpanded !== undefined &&
          // Allow single group expansion with accordion
          !(typeof config.defaultExpanded === 'number') &&
          !(typeof config.defaultExpanded === 'string') &&
          // Warn if true or array with multiple items
          (config.defaultExpanded === true ||
            (Array.isArray(config.defaultExpanded) && config.defaultExpanded.length > 1)),
      },
    ],
  };

  /**
   * Optional dependency on MultiSort for coordinated sort management.
   * When MultiSort is loaded, GroupingRows queries its sort model to determine
   * group header ordering. Without it, falls back to core sort state.
   */
  static override readonly dependencies = [
    { name: 'multiSort', required: false, reason: 'Queries sort model for coordinated group sorting' },
    { name: 'serverSide', required: false, reason: 'Consumes datasource events for lazy-loaded grouped data' },
  ];

  /** @internal */
  readonly name = 'groupingRows';
  /** @internal */
  override readonly styles = styles;

  /** @internal */
  protected override get defaultConfig(): Partial<GroupingRowsConfig> {
    return {
      defaultExpanded: false,
      showRowCount: true,
      indentWidth: 20,
      aggregators: {},
      animation: 'slide',
      accordion: false,
    };
  }

  // #region Internal State
  private expandedKeys: Set<string> = new Set();
  private flattenedRows: RenderRow[] = [];
  private isActive = false;
  private previousVisibleKeys = new Set<string>();
  private keysToAnimate = new Set<string>();
  /** Track if initial defaultExpanded has been applied */
  private hasAppliedDefaultExpanded = false;
  /**
   * Expansion override to apply on the next `processRows` rebuild instead of
   * — or in addition to — `config.defaultExpanded`. Set by `setGroupOn(fn, expanded)`
   * and by `expandAll()` / `collapseAll()` when the grouping config has just
   * changed (so `flattenedRows` is stale). Cleared once consumed by a rebuild.
   * See issue #335.
   */
  private pendingExpansion: DefaultExpandedValue | undefined;
  /**
   * Set by `setGroupOn` to mark the cached `flattenedRows` as stale until the
   * next `processRows` runs. While true, `expandAll`/`collapseAll` defer via
   * `pendingExpansion` instead of reading the stale snapshot. See issue #335.
   */
  private groupConfigDirty = false;
  /** Pre-defined group definitions (from config, setGroups(), or datasource:data) */
  private preDefinedGroups: GroupDefinition[] = [];
  /** Row data keyed by group key (from setGroupRows() or datasource:children) */
  private groupRowsMap = new Map<string, unknown[]>();
  /** Groups currently in a loading state */
  private loadingGroups = new Set<string>();
  /** Column fields that produce group values (depth 0, 1, ...). Cached for
   *  the `grouping:get-grouped-fields` query so MultiSort can filter them out. */
  private groupedFields: string[] = [];
  /** User-specified sort directions per group depth level. Toggled via
   *  header clicks on grouped columns. */
  private userGroupSortDirections = new Map<number, 1 | -1>();
  /**
   * Per-flatten-index ARIA position metadata for `aria-level` /
   * `aria-setsize` / `aria-posinset` (WAI-ARIA Treegrid pattern). Computed
   * once per `flattenedRows` rebuild by {@link computeFlatMeta}; consumed
   * by {@link afterRender} on every visible row.
   */
  private flatMeta: Array<{ level: number; setSize: number; posInSet: number }> = [];
  // #endregion

  // #region Animation

  /**
   * Get expand/collapse animation style from plugin config.
   * Uses base class isAnimationEnabled to respect grid-level settings.
   */
  private get animationStyle(): ExpandCollapseAnimation {
    if (!this.isAnimationEnabled) return false;
    return this.config.animation ?? 'slide';
  }

  // #endregion

  // #region Lifecycle

  /** @internal */
  override detach(): void {
    // Restore default `role="grid"` on the rows-body so the grid stays
    // ARIA-valid after the plugin is removed (template default lives in
    // `core/internal/dom-builder.ts`). See WAI-ARIA Treegrid pattern.
    const rowsBody = this.gridElement?.querySelector('.rows-body');
    rowsBody?.setAttribute('role', 'grid');

    this.expandedKeys.clear();
    this.flattenedRows = [];
    this.flatMeta = [];
    this.isActive = false;
    this.previousVisibleKeys.clear();
    this.keysToAnimate.clear();
    this.hasAppliedDefaultExpanded = false;
    this.pendingExpansion = undefined;
    this.groupConfigDirty = false;
    this.preDefinedGroups = [];
    this.groupRowsMap.clear();
    this.loadingGroups.clear();
    this.groupedFields = [];
    this.userGroupSortDirections.clear();
  }

  /**
   * Provide row height for group header rows.
   *
   * If `groupRowHeight` is configured, returns that value for group rows.
   * This allows the variable row height system to use known heights for
   * group headers without needing to measure them from the DOM.
   *
   * @param row - The row object (may be a group row)
   * @param _index - Index in the processed rows array (unused)
   * @returns Height in pixels for group rows, undefined for data rows
   *
   * @internal Plugin hook for variable row height support
   */
  override getRowHeight(row: unknown, _index: number): number | undefined {
    // Only provide height if groupRowHeight is configured
    if (this.config.groupRowHeight == null) return undefined;

    // Check if this is a group row
    if ((row as { __isGroupRow?: boolean }).__isGroupRow === true) {
      return this.config.groupRowHeight;
    }

    return undefined;
  }

  /**
   * Handle plugin queries.
   * @internal
   */
  override handleQuery(query: PluginQuery): unknown {
    if (query.type === 'canMoveRow') {
      // Group header rows cannot be reordered
      const row = query.context as { __isGroupRow?: boolean } | null | undefined;
      if (row?.__isGroupRow === true) {
        return false;
      }
    }
    if (query.type === 'grouping:get-grouped-fields') {
      return [...this.groupedFields];
    }
    if (query.type === 'datasource:viewport-mapping') {
      // Translate visible flat row indices → top-level group indices for ServerSide pagination
      const { viewportStart, viewportEnd } = query.context as ViewportMappingQuery;
      if (this.flattenedRows.length === 0) return undefined;
      // Only respond in pre-defined groups mode (when datasource data is being consumed)
      if (this.preDefinedGroups.length === 0 && !Array.isArray(this.config.groups)) return undefined;

      const activeGroups = this.getActiveGroups();
      const startNode = this.getTopLevelGroupIndex(viewportStart);
      const endNode = this.getTopLevelGroupIndex(viewportEnd) + 1; // exclusive
      const totalLoadedNodes = activeGroups.length;

      return { startNode, endNode, totalLoadedNodes } satisfies ViewportMappingResponse;
    }
    return undefined;
  }

  /**
   * Translate a flat row index to the top-level group index it belongs to.
   * Walks flattenedRows up to the given index and counts depth-0 groups encountered.
   */
  private getTopLevelGroupIndex(flatIndex: number): number {
    let groupIndex = -1;
    const clampedIndex = Math.min(flatIndex, this.flattenedRows.length - 1);
    for (let i = 0; i <= clampedIndex; i++) {
      const row = this.flattenedRows[i];
      if (row.kind === 'group' && row.depth === 0) {
        groupIndex++;
      }
    }
    return Math.max(0, groupIndex);
  }

  // #region Lifecycle

  /** @internal */
  override attach(grid: GridElement): void {
    super.attach(grid);

    // Listen for datasource:data from ServerSidePlugin — claim data as group definitions
    this.on('datasource:data', (detail: unknown) => {
      const d = detail as DataSourceDataDetail;
      if (!d.claimed) {
        d.claimed = true;
        // Interpret incoming rows as group definitions
        this.preDefinedGroups = d.rows as unknown as GroupDefinition[];
        this.groupRowsMap.clear();
        this.loadingGroups.clear();
        this.hasAppliedDefaultExpanded = false;
        this.requestRender();
      }
    });

    // Listen for datasource:children — consume child rows from ServerSide
    this.on('datasource:children', (detail: unknown) => {
      const d = detail as DataSourceChildrenDetail;
      if (d.context?.source !== 'grouping-rows') return;
      d.claimed = true;

      // Store the rows for the group and clear loading state
      const groupKey = d.context.groupKey as string;
      if (groupKey) {
        this.groupRowsMap.set(groupKey, d.rows);
        this.loadingGroups.delete(groupKey);
        this.requestRender();
      }
    });
  }

  /**
   * Intercept header clicks on grouped columns to toggle group sort direction.
   * Returns `true` for grouped columns to prevent MultiSort from handling them.
   * @internal
   */
  override onHeaderClick(event: HeaderClickEvent): boolean | void {
    const fieldIndex = this.groupedFields.indexOf(event.field);
    if (fieldIndex === -1) return; // Not a grouped column — let other plugins handle it

    if (!event.column.sortable) return;

    // The fieldIndex in groupedFields corresponds to the group depth level
    // (resolveGroupFields populates them in depth order: 0, 1, 2, ...)
    const targetDepth = fieldIndex;

    // Toggle direction: asc → desc → asc
    const current = this.userGroupSortDirections.get(targetDepth) ?? 1;
    this.userGroupSortDirections.set(targetDepth, current === 1 ? -1 : 1);

    this.requestRender();
    return true; // Prevent MultiSort from handling this column
  }
  // #endregion

  // #region Sort State Resolution

  /**
   * Build a sort-direction map for each group depth level by cross-referencing
   * the active sort state with the column fields that produce group values.
   *
   * Supports three direction sources (in priority order):
   * 1. User-set directions via header clicks on grouped columns
   * 2. MultiSort plugin model (for backwards compatibility / state restore)
   * 3. Core single-column sort state
   */
  private resolveGroupSortDirections(rows: readonly any[]): Map<number, 1 | -1> | undefined {
    const config = this.config;
    if (typeof config.groupOn !== 'function' || rows.length === 0) {
      this.groupedFields = [];
      return undefined;
    }

    // Discover depth → field mapping by sampling rows
    const columnFields = this.columns.map((c) => c.field);
    const depthToField = resolveGroupFields([...rows], config.groupOn, columnFields);

    // Cache grouped field names for the grouping:get-grouped-fields query
    this.groupedFields = [...depthToField.values()];

    if (depthToField.size === 0) return undefined;

    // Start with user-set directions (from header clicks on grouped columns)
    const directions = new Map<number, 1 | -1>(this.userGroupSortDirections);

    // Fill missing depths from MultiSort model or core sort state
    if (directions.size < depthToField.size) {
      const activeSorts = new Map<string, 1 | -1>();

      const multiSortResults = this.grid?.query?.('sort:get-model', null);
      if (Array.isArray(multiSortResults) && multiSortResults.length > 0) {
        const sortModel = multiSortResults[0] as Array<{ field: string; direction: 'asc' | 'desc' }>;
        if (Array.isArray(sortModel)) {
          for (const entry of sortModel) {
            activeSorts.set(entry.field, entry.direction === 'desc' ? -1 : 1);
          }
        }
      }

      if (activeSorts.size === 0) {
        const gridHost = this.grid as unknown as { _sortState?: { field: string; direction: 1 | -1 } | null };
        if (gridHost._sortState) {
          activeSorts.set(gridHost._sortState.field, gridHost._sortState.direction);
        }
      }

      for (const [depth, field] of depthToField) {
        if (!directions.has(depth)) {
          const dir = activeSorts.get(field);
          if (dir !== undefined) {
            directions.set(depth, dir);
          }
        }
      }
    }

    return directions.size > 0 ? directions : undefined;
  }

  // #endregion

  // #region Hooks

  /**
   * Auto-detect grouping configuration from grid config.
   * Called by plugin system to determine if plugin should activate.
   */
  static detect(rows: readonly any[], config: any): boolean {
    return (
      typeof config?.groupOn === 'function' ||
      typeof config?.enableRowGrouping === 'boolean' ||
      Array.isArray(config?.groups)
    );
  }

  /** @internal */
  override processRows(rows: readonly any[]): any[] {
    // Snapshot + clear deferred-expansion state at the top so any early-return
    // (no groupOn, empty rows, etc.) doesn't leave a stale `pendingExpansion`
    // behind to be (mis-)applied to the next unrelated rebuild.
    const pendingExpansion = this.pendingExpansion;
    this.pendingExpansion = undefined;
    this.groupConfigDirty = false;

    // Pre-defined groups path — use external group structure instead of groupOn analysis
    if (this.preDefinedGroups.length > 0 || Array.isArray(this.config.groups)) {
      if (this.preDefinedGroups.length > 0 || (Array.isArray(this.config.groups) && this.config.groups.length > 0)) {
        return this.processPreDefinedGroups();
      }
      this.isActive = false;
      this.flattenedRows = [];
      return [];
    }

    const config = this.config;

    // Check if grouping is configured
    if (typeof config.groupOn !== 'function') {
      this.isActive = false;
      this.flattenedRows = [];
      return [...rows];
    }

    // First build: get structure to know all group keys
    // (needed for index-based defaultExpanded)
    const groupSortDirections = this.resolveGroupSortDirections(rows);
    const initialBuild = buildGroupedRowModel({
      rows: [...rows],
      config: config,
      expanded: new Set(), // Empty to get all root groups
      groupSortDirections,
    });

    // If no grouping produced, return original rows
    if (initialBuild.length === 0) {
      this.isActive = false;
      this.flattenedRows = [];
      return [...rows];
    }

    // Determine which expansion config to resolve against the *fresh* group
    // keys. A pending value (from `setGroupOn(fn, expanded)` or a deferred
    // `expandAll`/`collapseAll` after `setGroupOn`) takes priority and is
    // applied unconditionally — including the empty case (collapseAll). Falls
    // back to the first-render `defaultExpanded` resolution.
    let expansionConfig: DefaultExpandedValue | undefined;
    const isPendingApply = pendingExpansion !== undefined;
    if (isPendingApply) {
      expansionConfig = pendingExpansion;
    } else if (!this.hasAppliedDefaultExpanded && this.expandedKeys.size === 0 && config.defaultExpanded !== false) {
      expansionConfig = config.defaultExpanded ?? false;
    }

    let initialExpanded: Set<string> | undefined;
    if (expansionConfig !== undefined) {
      const allKeys = getGroupKeys(initialBuild);
      initialExpanded = resolveDefaultExpanded(expansionConfig, allKeys);
      if (isPendingApply) {
        // Pending overrides any stale keys — apply unconditionally.
        this.expandedKeys = new Set(initialExpanded);
        this.hasAppliedDefaultExpanded = true;
      } else if (initialExpanded.size > 0) {
        this.expandedKeys = new Set(initialExpanded);
        this.hasAppliedDefaultExpanded = true;
      }
    }

    // Build with proper expanded state
    const grouped = buildGroupedRowModel({
      rows: [...rows],
      config: config,
      expanded: this.expandedKeys,
      initialExpanded,
      groupSortDirections,
    });

    this.isActive = true;
    this.flattenedRows = grouped;
    this.computeFlatMeta();

    // Notify subscribers when a deferred expansion has been applied so React/
    // host code can mirror the new state without a follow-up call. Use
    // `broadcast` so DOM listeners (`grid.addEventListener('group-toggle')`)
    // see the change too — matches the manifest contract for `group-toggle`
    // and `toggle()`'s emit pattern.
    if (isPendingApply) {
      this.broadcast<GroupToggleDetail>('group-toggle', { expandedKeys: [...this.expandedKeys] });
    }

    // Track which data rows are newly visible (for animation)
    this.keysToAnimate.clear();
    const currentVisibleKeys = new Set<string>();
    grouped.forEach((item, idx) => {
      if (item.kind === 'data') {
        const key = `data-${idx}`;
        currentVisibleKeys.add(key);
        if (!this.previousVisibleKeys.has(key)) {
          this.keysToAnimate.add(key);
        }
      }
    });
    this.previousVisibleKeys = currentVisibleKeys;

    // Return flattened rows for rendering
    // The grid will need to handle group rows specially
    return grouped.map((item) => {
      if (item.kind === 'group') {
        return {
          __isGroupRow: true,
          __groupKey: item.key,
          __groupValue: item.value,
          __groupDepth: item.depth,
          __groupRows: item.rows,
          __groupExpanded: item.expanded,
          __groupRowCount: getGroupRowCount(item),
          // Cache key for variable row height support - survives expand/collapse
          __rowCacheKey: `group:${item.key}`,
        };
      }
      return item.row;
    });
  }

  /** @internal */
  override onCellClick(event: CellClickEvent): boolean | void {
    const row = event.row as Record<string, unknown> | undefined;

    // Check if this is a group row toggle
    if (row?.__isGroupRow) {
      const target = event.originalEvent.target as HTMLElement;
      if (target?.closest(`.${GridClasses.GROUP_TOGGLE}`)) {
        this.toggle(row.__groupKey as string);
        return true; // Prevent default
      }
    }
  }

  /** @internal */
  override onKeyDown(event: KeyboardEvent): boolean | void {
    // SPACE toggles expansion on group rows
    if (event.key !== ' ') return;

    const focusRow = this.grid._focusRow;
    const row = this.rows[focusRow] as Record<string, unknown> | undefined;

    // Only handle SPACE on group rows
    if (!row?.__isGroupRow) return;

    event.preventDefault();
    this.toggle(row.__groupKey as string);

    // Restore focus styling after render completes via render pipeline
    this.requestRenderWithFocus();
    return true;
  }

  /**
   * Render a row. Returns true if we handled the row (group row or loading placeholder), false otherwise.
   * @internal
   */
  override renderRow(row: any, rowEl: HTMLElement, _rowIndex: number): boolean {
    // Handle loading placeholder rows for pre-defined groups
    // Uses the grid's built-in row loading API for consistent, customizable spinners
    if (row?.__loading === true && row?.__groupKey) {
      rowEl.className = 'data-grid-row';
      (rowEl as RowElementInternal).__isCustomRow = true;
      rowEl.innerHTML = '';
      const cell = document.createElement('div');
      cell.className = 'cell';
      cell.style.gridColumn = '1 / -1';
      cell.setAttribute('role', 'gridcell');
      cell.textContent = '\u00A0'; // &nbsp; for row height
      rowEl.appendChild(cell);
      setRowLoadingState(rowEl, true);
      return true;
    }

    // Only handle group rows
    if (!row?.__isGroupRow) {
      return false;
    }

    const config = this.config;

    // If a custom renderer is provided, use it
    if (config.groupRowRenderer) {
      const toggleExpand = () => {
        this.toggle(row.__groupKey);
      };

      const result = config.groupRowRenderer({
        key: row.__groupKey,
        value: row.__groupValue,
        depth: row.__groupDepth,
        rows: row.__groupRows,
        expanded: row.__groupExpanded,
        toggleExpand,
      });

      if (result) {
        rowEl.className = 'data-grid-row group-row';
        (rowEl as RowElementInternal).__isCustomRow = true; // Mark for proper class reset on recycle
        rowEl.setAttribute('data-group-depth', String(row.__groupDepth));
        if (typeof result === 'string') {
          rowEl.innerHTML = result;
        } else {
          rowEl.innerHTML = '';
          rowEl.appendChild(result);
        }
        return true;
      }
    }

    // Helper to toggle expansion
    const handleToggle = () => {
      this.toggle(row.__groupKey);
    };

    // Default group row rendering - keep data-grid-row class for focus/keyboard navigation
    rowEl.className = 'data-grid-row group-row';
    (rowEl as RowElementInternal).__isCustomRow = true; // Mark for proper class reset on recycle
    rowEl.setAttribute('data-group-depth', String(row.__groupDepth));
    rowEl.setAttribute('role', 'row');
    rowEl.setAttribute('aria-expanded', String(row.__groupExpanded));
    // Public theming hook — see also TreePlugin / MasterDetailPlugin.
    rowEl.classList.toggle('tbw-row-expanded', !!row.__groupExpanded);
    // WAI-ARIA Treegrid: group rows announce their hierarchical position.
    const groupMeta = this.flatMeta[_rowIndex];
    if (groupMeta) {
      rowEl.setAttribute('aria-level', String(groupMeta.level));
      rowEl.setAttribute('aria-setsize', String(groupMeta.setSize));
      rowEl.setAttribute('aria-posinset', String(groupMeta.posInSet));
    }
    // Use CSS variable for depth-based indentation
    rowEl.style.setProperty('--tbw-group-depth', String(row.__groupDepth || 0));
    if (config.indentWidth !== undefined) {
      rowEl.style.setProperty('--tbw-group-indent-width', `${config.indentWidth}px`);
    }
    // Clear any inline height from previous use (e.g., responsive card mode sets height: auto)
    // This ensures group rows use CSS-defined height, not stale inline styles from recycled elements
    rowEl.style.height = '';
    rowEl.innerHTML = '';

    const isFullWidth = config.fullWidth !== false; // default true

    if (isFullWidth) {
      this.renderFullWidthGroupRow(row, rowEl, handleToggle);
    } else {
      this.renderPerColumnGroupRow(row, rowEl, handleToggle);
    }

    return true;
  }

  /** @internal */
  override afterRender(): void {
    const body = this.gridElement?.querySelector('.rows');
    if (!body) return;

    // Hierarchy is now in play \u2192 swap the rows-body role from `grid` to
    // `treegrid` per WAI-ARIA so per-row level/setsize/posinset are valid
    // in context. Idempotent on the hot path.
    const rowsBody = this.gridElement?.querySelector('.rows-body');
    if (rowsBody && rowsBody.getAttribute('role') !== 'treegrid') {
      rowsBody.setAttribute('role', 'treegrid');
    }

    // Apply ARIA position metadata to every visible DATA row (group rows are
    // populated by `renderRow` directly since they bypass the default cell
    // template that exposes `data-row`).
    for (const rowEl of body.querySelectorAll('.data-grid-row:not(.group-row)')) {
      const cell = rowEl.querySelector('.cell[data-row]');
      const idx = cell ? parseInt(cell.getAttribute('data-row') ?? '-1', 10) : -1;
      const meta = this.flatMeta[idx];
      if (!meta) continue;
      rowEl.setAttribute('aria-level', String(meta.level));
      rowEl.setAttribute('aria-setsize', String(meta.setSize));
      rowEl.setAttribute('aria-posinset', String(meta.posInSet));
    }

    const style = this.animationStyle;
    if (style === false || this.keysToAnimate.size === 0) return;

    const animClass = style === 'fade' ? 'tbw-group-fade-in' : 'tbw-group-slide-in';
    for (const rowEl of body.querySelectorAll('.data-grid-row:not(.group-row)')) {
      const cell = rowEl.querySelector('.cell[data-row]');
      const idx = cell ? parseInt(cell.getAttribute('data-row') ?? '-1', 10) : -1;
      const item = this.flattenedRows[idx];
      const key = item?.kind === 'data' ? `data-${idx}` : undefined;

      if (key && this.keysToAnimate.has(key)) {
        rowEl.classList.add(animClass);
        rowEl.addEventListener('animationend', () => rowEl.classList.remove(animClass), { once: true });
      }
    }
    this.keysToAnimate.clear();
  }

  /**
   * Compute per-row ARIA hierarchy metadata (`aria-level` / `aria-setsize` /
   * `aria-posinset`) from `flattenedRows`. Two-pass over the flat list:
   * first pass tallies group siblings per (parent-prefix, depth) and counts
   * the data rows that follow each group header; second pass assigns per-row
   * meta. Called once per `flattenedRows` rebuild (not per render).
   */
  private computeFlatMeta(): void {
    const flat = this.flattenedRows;
    const meta: Array<{ level: number; setSize: number; posInSet: number }> = new Array(flat.length);

    // Pass 1: count group siblings per (parent-prefix, depth) and direct
    // data-row children per group (used as `setSize` for data rows).
    const groupSiblingCount = new Map<string, number>();
    const groupDataCount = new Map<string, number>();
    let activeGroup: GroupRowModelItem | null = null;
    let activeDataCount = 0;
    for (const item of flat) {
      if (item.kind === 'group') {
        if (activeGroup) groupDataCount.set(activeGroup.key, activeDataCount);
        activeGroup = item;
        activeDataCount = 0;
        const parts = item.key.split('||');
        const parent = parts.slice(0, -1).join('||');
        const k = `${parent}@${item.depth}`;
        groupSiblingCount.set(k, (groupSiblingCount.get(k) ?? 0) + 1);
      } else {
        activeDataCount++;
      }
    }
    if (activeGroup) groupDataCount.set(activeGroup.key, activeDataCount);

    // Pass 2: assign per-row level/setsize/posinset.
    const groupSiblingPos = new Map<string, number>();
    let currentGroup: GroupRowModelItem | null = null;
    let dataPos = 0;
    for (let i = 0; i < flat.length; i++) {
      const item = flat[i];
      if (item.kind === 'group') {
        const parts = item.key.split('||');
        const parent = parts.slice(0, -1).join('||');
        const k = `${parent}@${item.depth}`;
        const pos = (groupSiblingPos.get(k) ?? 0) + 1;
        groupSiblingPos.set(k, pos);
        meta[i] = { level: item.depth + 1, setSize: groupSiblingCount.get(k) ?? 1, posInSet: pos };
        currentGroup = item;
        dataPos = 0;
      } else {
        dataPos++;
        const baseDepth = currentGroup?.depth ?? -1;
        const setSize = currentGroup ? (groupDataCount.get(currentGroup.key) ?? 1) : flat.length;
        meta[i] = { level: baseDepth + 2, setSize, posInSet: dataPos };
      }
    }

    this.flatMeta = meta;
  }
  // #endregion

  // #region Pre-Defined Groups

  /**
   * Build the row model from pre-defined group definitions.
   * Used when `groups` config or `setGroups()` provides external group structure.
   */
  private processPreDefinedGroups(): any[] {
    const groups =
      this.preDefinedGroups.length > 0
        ? this.preDefinedGroups
        : Array.isArray(this.config.groups)
          ? this.config.groups
          : [];

    if (groups.length === 0) {
      this.isActive = false;
      this.flattenedRows = [];
      return [];
    }

    // Resolve defaultExpanded on first render only
    if (!this.hasAppliedDefaultExpanded && this.expandedKeys.size === 0 && this.config.defaultExpanded !== false) {
      const allKeys = this.collectGroupKeys(groups);
      const initialExpanded = resolveDefaultExpanded(this.config.defaultExpanded ?? false, allKeys);
      if (initialExpanded.size > 0) {
        this.expandedKeys = new Set(initialExpanded);
        this.hasAppliedDefaultExpanded = true;
      }
    }

    const grouped = buildPreDefinedGroupModel({
      groups,
      expanded: this.expandedKeys,
      groupRows: this.groupRowsMap,
      loadingGroups: this.loadingGroups,
    });

    this.isActive = true;
    this.flattenedRows = grouped;
    this.computeFlatMeta();

    // Track visible data rows for animation
    this.keysToAnimate.clear();
    const currentVisibleKeys = new Set<string>();
    grouped.forEach((item, idx) => {
      if (item.kind === 'data') {
        const key = `data-${idx}`;
        currentVisibleKeys.add(key);
        if (!this.previousVisibleKeys.has(key)) {
          this.keysToAnimate.add(key);
        }
      }
    });
    this.previousVisibleKeys = currentVisibleKeys;

    // Return flattened rows for rendering
    return grouped.map((item) => {
      if (item.kind === 'group') {
        // Look up the pre-defined group to get rowCount from server
        const groupDef = this.findGroupDefinition(groups, item.key);
        const rowCount = groupDef?.rowCount ?? item.rows.length;
        return {
          __isGroupRow: true,
          __groupKey: item.key,
          __groupValue: item.value,
          __groupDepth: item.depth,
          __groupRows: item.rows,
          __groupExpanded: item.expanded,
          __groupRowCount: rowCount,
          __rowCacheKey: `group:${item.key}`,
        };
      }
      return item.row;
    });
  }

  /**
   * Collect all group keys from a pre-defined group tree.
   */
  private collectGroupKeys(groups: GroupDefinition[]): string[] {
    const keys: string[] = [];
    for (const g of groups) {
      keys.push(g.key);
      if (g.children?.length) {
        keys.push(...this.collectGroupKeys(g.children));
      }
    }
    return keys;
  }

  /**
   * Get the active group definitions (pre-defined or empty).
   */
  private getActiveGroups(): GroupDefinition[] {
    if (this.preDefinedGroups.length > 0) return this.preDefinedGroups;
    return Array.isArray(this.config.groups) ? this.config.groups : [];
  }

  /**
   * Find a group definition by key in the pre-defined group tree.
   */
  private findGroupDefinition(groups: GroupDefinition[], key: string): GroupDefinition | undefined {
    for (const g of groups) {
      if (g.key === key) return g;
      if (g.children?.length) {
        const found = this.findGroupDefinition(g.children, key);
        if (found) return found;
      }
    }
    return undefined;
  }
  // #endregion

  // #region Private Rendering Helpers

  /**
   * Create a toggle button for expanding/collapsing a group.
   */
  private createToggleButton(expanded: boolean, handleToggle: () => void): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `${GridClasses.GROUP_TOGGLE}${expanded ? ` ${GridClasses.EXPANDED}` : ''}`;
    btn.setAttribute('aria-label', expanded ? 'Collapse group' : 'Expand group');
    this.setIcon(btn, expanded ? 'collapse' : 'expand');
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      handleToggle();
    });
    return btn;
  }

  /**
   * Get the formatted label text for a group.
   */
  private getGroupLabelText(value: unknown, depth: number, key: string): string {
    const config = this.config;
    return config.formatLabel ? config.formatLabel(value, depth, key) : String(value);
  }

  private renderFullWidthGroupRow(row: any, rowEl: HTMLElement, handleToggle: () => void): void {
    const config = this.config;
    const aggregators = config.aggregators ?? {};
    const groupRows = row.__groupRows ?? [];

    // Full-width mode: single spanning cell with toggle + label + count + aggregates
    const cell = document.createElement('div');
    cell.className = 'cell group-full';
    cell.style.gridColumn = '1 / -1';
    cell.setAttribute('role', 'gridcell');
    cell.setAttribute('data-col', '0'); // Required for focus/click delegation

    // Toggle button
    cell.appendChild(this.createToggleButton(row.__groupExpanded, handleToggle));

    // Group label
    const label = document.createElement('span');
    label.className = GridClasses.GROUP_LABEL;
    label.textContent = this.getGroupLabelText(row.__groupValue, row.__groupDepth || 0, row.__groupKey);
    cell.appendChild(label);

    // Row count
    if (config.showRowCount !== false) {
      const count = document.createElement('span');
      count.className = GridClasses.GROUP_COUNT;
      count.textContent = `(${row.__groupRowCount ?? row.__groupRows?.length ?? 0})`;
      cell.appendChild(count);
    }

    // Render aggregates if configured
    const aggregatorEntries = Object.entries(aggregators);
    if (aggregatorEntries.length > 0) {
      const aggregatesContainer = document.createElement('span');
      aggregatesContainer.className = 'group-aggregates';

      for (const [field, aggRef] of aggregatorEntries) {
        const col = this.columns.find((c) => c.field === field);
        const result = aggregatorRegistry.run(aggRef, groupRows, field, col);
        if (result != null) {
          const aggSpan = document.createElement('span');
          aggSpan.className = 'group-aggregate';
          aggSpan.setAttribute('data-field', field);
          // Use column header as label if available
          const colHeader = col?.header ?? field;
          aggSpan.textContent = `${colHeader}: ${result}`;
          aggregatesContainer.appendChild(aggSpan);
        }
      }

      if (aggregatesContainer.children.length > 0) {
        cell.appendChild(aggregatesContainer);
      }
    }

    rowEl.appendChild(cell);
  }

  private renderPerColumnGroupRow(row: any, rowEl: HTMLElement, handleToggle: () => void): void {
    const config = this.config;
    const aggregators = config.aggregators ?? {};
    const columns = this.columns;
    const groupRows = row.__groupRows ?? [];

    // Get grid template from the grid element
    const bodyEl = this.gridElement?.querySelector('.body') as HTMLElement | null;
    const gridTemplate = bodyEl?.style.gridTemplateColumns || '';
    if (gridTemplate) {
      rowEl.style.display = 'grid';
      rowEl.style.gridTemplateColumns = gridTemplate;
    }

    // Track whether we've rendered the toggle button yet (should be in first non-expander column)
    let toggleRendered = false;

    columns.forEach((col, colIdx) => {
      const cell = document.createElement('div');
      cell.className = 'cell group-cell';
      cell.setAttribute('data-col', String(colIdx));
      cell.setAttribute('role', 'gridcell');

      // Skip expander columns (they're handled by other plugins like MasterDetail/Tree)
      // but still render an empty cell to maintain grid structure
      if (isExpanderColumn(col)) {
        cell.setAttribute('data-field', col.field);
        rowEl.appendChild(cell);
        return;
      }

      // First non-expander column gets the toggle button + label
      if (!toggleRendered) {
        toggleRendered = true;
        cell.appendChild(this.createToggleButton(row.__groupExpanded, handleToggle));

        const label = document.createElement('span');
        const firstColAgg = aggregators[col.field];
        if (firstColAgg) {
          const aggResult = aggregatorRegistry.run(firstColAgg, groupRows, col.field, col);
          label.textContent = aggResult != null ? String(aggResult) : String(row.__groupValue);
        } else {
          label.textContent = this.getGroupLabelText(row.__groupValue, row.__groupDepth || 0, row.__groupKey);
        }
        cell.appendChild(label);

        if (config.showRowCount !== false) {
          const count = document.createElement('span');
          count.className = GridClasses.GROUP_COUNT;
          count.textContent = ` (${groupRows.length})`;
          cell.appendChild(count);
        }
      } else {
        // Other columns: run aggregator if defined
        const aggRef = aggregators[col.field];
        if (aggRef) {
          const result = aggregatorRegistry.run(aggRef, groupRows, col.field, col);
          cell.textContent = result != null ? String(result) : '';
        } else {
          cell.textContent = '';
        }
      }

      rowEl.appendChild(cell);
    });
  }
  // #endregion

  // #region Public API

  /**
   * Expand all groups.
   *
   * Safe to call immediately after {@link setGroupOn}: when the grouping
   * config has just changed, the cached `flattenedRows` is stale, so the
   * call defers until the next render rebuilds the group model against the
   * new `groupOn` (issue #335). The `group-toggle` event is emitted once
   * the new keys are known.
   */
  expandAll(): void {
    if (this.groupConfigDirty) {
      // flattenedRows is stale — defer until processRows rebuilds it. Clear
      // the cached set so callers don't observe stale keys via
      // `getExpandedGroups()` between this call and the next render.
      this.pendingExpansion = true;
      this.expandedKeys = new Set();
      this.requestRender();
      return;
    }
    this.expandedKeys = expandAllGroups(this.flattenedRows);
    this.broadcast<GroupToggleDetail>('group-toggle', { expandedKeys: [...this.expandedKeys] });
    this.requestRender();
  }

  /**
   * Collapse all groups.
   *
   * Safe to call immediately after {@link setGroupOn}: defers until the next
   * render rebuilds the group model so the collapse targets the new groups
   * rather than a stale snapshot (issue #335).
   */
  collapseAll(): void {
    if (this.groupConfigDirty) {
      this.pendingExpansion = false;
      this.expandedKeys = new Set();
      this.requestRender();
      return;
    }
    this.expandedKeys = collapseAllGroups();
    this.broadcast<GroupToggleDetail>('group-toggle', { expandedKeys: [...this.expandedKeys] });
    this.requestRender();
  }

  /**
   * Toggle expansion of a specific group.
   * In accordion mode, expanding a group will collapse all sibling groups.
   * @param key - The group key to toggle
   */
  toggle(key: string): void {
    const isExpanding = !this.expandedKeys.has(key);
    const config = this.config;

    // Find the group to get its depth for accordion mode
    const group = this.flattenedRows.find((r) => r.kind === 'group' && r.key === key) as GroupRowModelItem | undefined;

    // In accordion mode, collapse sibling groups when expanding
    if (config.accordion && isExpanding && group) {
      const newKeys = new Set<string>();
      // Keep only ancestors (keys that are prefixes of the current key) and the current key
      for (const existingKey of this.expandedKeys) {
        // Check if existingKey is an ancestor of the toggled key
        // Ancestors have composite keys that are prefixes of child keys (separated by '||')
        if (key.startsWith(existingKey + '||') || existingKey.startsWith(key + '||')) {
          // This is an ancestor or descendant - keep it only if ancestor
          if (key.startsWith(existingKey + '||')) {
            newKeys.add(existingKey);
          }
        } else {
          // Check depth - only keep groups at different depths
          const existingGroup = this.flattenedRows.find((r) => r.kind === 'group' && r.key === existingKey) as
            | GroupRowModelItem
            | undefined;
          if (existingGroup && existingGroup.depth !== group.depth) {
            newKeys.add(existingKey);
          }
        }
      }
      newKeys.add(key);
      this.expandedKeys = newKeys;
    } else {
      this.expandedKeys = toggleGroupExpansion(this.expandedKeys, key);
    }

    this.broadcast<GroupToggleDetail>('group-toggle', {
      key,
      expanded: this.expandedKeys.has(key),
      value: group?.value,
      depth: group?.depth ?? 0,
      expandedKeys: [...this.expandedKeys],
    });

    // Emit group-expand/group-collapse events for pre-defined mode
    const activeGroups = this.getActiveGroups();
    if (activeGroups.length > 0) {
      const groupPath = getGroupPath(activeGroups, key);
      if (isExpanding) {
        this.emit<GroupExpandDetail>('group-expand', { groupKey: key, groupPath });

        // Request child rows via unified DataSource if not already cached
        if (!this.groupRowsMap.has(key)) {
          const groupDef = this.findGroupDefinition(activeGroups, key);
          if (groupDef) {
            this.loadingGroups.add(key);
            this.grid?.query?.('datasource:fetch-children', {
              context: { source: 'grouping-rows', groupKey: key, group: groupDef, groupPath },
            } satisfies FetchChildrenQuery);
          }
        }
      } else {
        this.emit<GroupCollapseDetail>('group-collapse', { groupKey: key, groupPath });
      }
    }

    // Announce group state change for screen readers
    const expanded = this.expandedKeys.has(key);
    const groupName = group?.value != null ? String(group.value) : key;
    if (expanded) {
      const rowCount = group?.rows?.length ?? 0;
      announce(this.gridElement, getA11yMessage(this.gridElement, 'groupExpanded', groupName, rowCount));
    } else {
      announce(this.gridElement, getA11yMessage(this.gridElement, 'groupCollapsed', groupName));
    }

    this.requestRender();
  }

  /**
   * Check if a specific group is expanded.
   * @param key - The group key to check
   * @returns Whether the group is expanded
   */
  isExpanded(key: string): boolean {
    return this.expandedKeys.has(key);
  }

  /**
   * Expand a specific group.
   * @param key - The group key to expand
   */
  expand(key: string): void {
    if (!this.expandedKeys.has(key)) {
      this.expandedKeys = new Set([...this.expandedKeys, key]);
      this.requestRender();
    }
  }

  /**
   * Collapse a specific group.
   * @param key - The group key to collapse
   */
  collapse(key: string): void {
    if (this.expandedKeys.has(key)) {
      const newKeys = new Set(this.expandedKeys);
      newKeys.delete(key);
      this.expandedKeys = newKeys;
      this.requestRender();
    }
  }

  /**
   * Get the current group state.
   * @returns Group state information
   */
  getGroupState(): GroupState {
    const groupRows = this.flattenedRows.filter((r) => r.kind === 'group');
    return {
      isActive: this.isActive,
      expandedCount: this.expandedKeys.size,
      totalGroups: groupRows.length,
      expandedKeys: [...this.expandedKeys],
    };
  }

  /**
   * Get the total count of visible rows (including group headers).
   * @returns Number of visible rows
   */
  getRowCount(): number {
    return this.flattenedRows.length;
  }

  /**
   * Refresh the grouped row model.
   * Call this after modifying groupOn or other config options.
   */
  refreshGroups(): void {
    this.requestRender();
  }

  /**
   * Get current expanded group keys.
   * @returns Array of expanded group keys
   */
  getExpandedGroups(): string[] {
    return [...this.expandedKeys];
  }

  /**
   * Get the flattened row model.
   * @returns Array of render rows (groups + data rows)
   */
  getFlattenedRows(): RenderRow[] {
    return this.flattenedRows;
  }

  /**
   * Check if grouping is currently active.
   * @returns Whether grouping is active
   */
  isGroupingActive(): boolean {
    return this.isActive;
  }

  /**
   * Set the groupOn function dynamically.
   *
   * Optionally specify the initial expansion state to apply against the new
   * group set. Without it, the previous `expandedKeys` are kept (and most
   * likely won't match the new keys, so groups land collapsed). With it,
   * the new groups are expanded synchronously on the next render — no need
   * to chain `setGroupOn(fn); expandAll()` or wait for `requestAnimationFrame`.
   *
   * If you do call `expandAll()` / `collapseAll()` separately right after
   * `setGroupOn`, those calls automatically defer to the same mechanism so
   * they target the new groups rather than the stale snapshot. See issue #335.
   *
   * @param fn - The groupOn function or undefined to disable
   * @param expanded - Optional initial expansion for the new grouping:
   *   - `true`: expand all new groups
   *   - `false`: collapse all new groups
   *   - `string` / `string[]`: expand specific group key(s)
   *   - `number`: expand the group at this index
   *   When omitted, existing `expandedKeys` are preserved (legacy behavior).
   *
   * @example Group by a new field and expand everything
   * ```ts
   * plugin.setGroupOn((row) => row.counterparty, true);
   * ```
   *
   * @example Group by a new field but keep all groups collapsed
   * ```ts
   * plugin.setGroupOn((row) => row.region, false);
   * ```
   */
  setGroupOn(fn: ((row: any) => any[] | any | null | false) | undefined, expanded?: DefaultExpandedValue): void {
    (this.config as GroupingRowsConfig).groupOn = fn;
    this.groupConfigDirty = true;
    if (expanded !== undefined) {
      this.pendingExpansion = expanded;
      this.expandedKeys = new Set();
      this.hasAppliedDefaultExpanded = false;
    }
    this.requestRender();
  }

  // --- Pre-defined group API ---

  /**
   * Replace auto-detected groups with an externally provided group structure.
   *
   * When groups are set, the plugin switches to pre-defined mode — `groupOn`
   * is ignored and the plugin renders the provided group headers instead.
   * Row data for each group must be populated via {@link setGroupRows}.
   *
   * @param groups - Array of group definitions, or empty array to clear
   */
  setGroups(groups: GroupDefinition[]): void {
    this.preDefinedGroups = groups;
    this.groupRowsMap.clear();
    this.loadingGroups.clear();
    this.expandedKeys.clear();
    this.hasAppliedDefaultExpanded = false;
    this.requestRender();
  }

  /**
   * Get the current pre-defined group structure.
   *
   * Returns the groups set via {@link setGroups}, received from `datasource:data`,
   * or the `groups` config array. Returns an empty array when using `groupOn`-based grouping.
   *
   * @returns Current group definitions
   */
  getGroups(): GroupDefinition[] {
    if (this.preDefinedGroups.length > 0) return [...this.preDefinedGroups];
    return Array.isArray(this.config.groups) ? [...this.config.groups] : [];
  }

  /**
   * Populate row data for an expanded group.
   *
   * Call this in response to a `group-expand` event after fetching rows
   * from the server. The plugin will re-render to show the rows.
   *
   * When `ServerSidePlugin` is loaded, this is handled automatically via
   * `datasource:children` events — you only need this for the imperative API.
   *
   * @param groupKey - The group key to populate
   * @param rows - The row data for this group
   */
  setGroupRows(groupKey: string, rows: unknown[]): void {
    this.groupRowsMap.set(groupKey, rows);
    this.loadingGroups.delete(groupKey);
    this.requestRender();
  }

  /**
   * Toggle loading indicator for a group.
   *
   * When loading is true, the group shows a loading spinner instead of row data.
   * Call with `false` after rows are loaded (also cleared by {@link setGroupRows}).
   *
   * @param groupKey - The group key
   * @param loading - Whether the group is loading
   */
  setGroupLoading(groupKey: string, loading: boolean): void {
    if (loading) {
      this.loadingGroups.add(groupKey);
    } else {
      this.loadingGroups.delete(groupKey);
    }
    this.requestRender();
  }

  /**
   * Clear cached row data for one or all groups.
   *
   * Use when the server data has changed and groups need to be re-fetched
   * on next expand.
   *
   * @param groupKey - Specific group key to clear, or omit to clear all
   */
  clearGroupRows(groupKey?: string): void {
    if (groupKey != null) {
      this.groupRowsMap.delete(groupKey);
    } else {
      this.groupRowsMap.clear();
    }
    this.requestRender();
  }
  // #endregion
}
